import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Typography, Space, Tag, message, Layout, Tooltip, Button, Badge } from 'antd';
import {
    TeamOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    PhoneOutlined,
    DashboardOutlined,
    SyncOutlined,
    SafetyCertificateOutlined,
    CustomerServiceOutlined,
    MessageOutlined,
    HistoryOutlined
} from '@ant-design/icons';
import { dashboardService } from '../services/api';
import { useSoftphone } from '../context/SoftphoneContext';

const { Title, Text } = Typography;
const { Content } = Layout;


const RealTimeTimer = ({ start, answer, waitSeconds, formatTime }) => {
    const [now, setNow] = useState(new Date());

    useEffect(() => {
        const interval = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(interval);
    }, []);

    const startTime = start ? new Date(start.replace(' ', 'T')) : null;
    const answerTime = answer ? new Date(answer.replace(' ', 'T')) : null;

    // Tiempo de espera
    let waitText = '00:00:00';
    if (waitSeconds !== null && waitSeconds !== undefined && parseInt(waitSeconds) > 0) {
        waitText = formatTime(waitSeconds);
    } else if (startTime) {
        const diff = Math.floor((now - startTime) / 1000);
        waitText = formatTime(diff > 0 ? diff : 0);
    }

    // Tiempo hablado
    let talkText = '00:00:00';
    if (answerTime) {
        const diff = Math.floor((now - answerTime) / 1000);
        talkText = formatTime(diff > 0 ? diff : 0);
    } else {
        talkText = <Text type="secondary" style={{ fontStyle: 'italic', fontSize: '10px' }}>Timbrando/esperando</Text>;
    }

    return (
        <Space direction="vertical" size={0}>
            <Text type="secondary" style={{ fontSize: '11px' }}>Espera: {waitText}</Text>
            <Text type="success" style={{ fontSize: '11px' }}>Hablado: {talkText}</Text>
        </Space>
    );
};

const DashboardPage = () => {
    const [loading, setLoading] = useState(true);
    const [metrics, setMetrics] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(new Date());
    const [isUpdating, setIsUpdating] = useState(false);
    const { makeCall, status: softphoneStatus, inCall, isCalling } = useSoftphone();
    const softphoneEnabled = softphoneStatus === 'registered';
    const canGlobalCall = softphoneEnabled && !inCall && !isCalling;

    const formatTime = (seconds) => {
        if (!seconds) return '00:00:00';
        seconds = parseInt(seconds, 10);
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        return [h, m, s].map(v => v < 10 ? '0' + v : v).join(':');
    };

    const getStatusTag = (status) => {
        let color = 'default';
        let label = status || '';
        if (status === 'answered') { color = 'success'; label = 'Contestada'; }
        if (status === 'no_answer') { color = 'warning'; label = 'No Contesta'; }
        if (status === 'busy') { color = 'error'; label = 'Ocupado'; }
        if (status === 'failed') { color = 'error'; label = 'Fallida'; }
        if (status === 'abandoned') { color = 'magenta'; label = 'Abandonada'; }
        if (status === 'rejected') { color = 'error'; label = 'Rechazada'; }
        if (status === 'in_queue') { color = 'processing'; label = 'En Cola'; }
        if (status === 'in_call') { color = 'processing'; label = 'En Llamada'; }
        return <Tag color={color} style={{ fontSize: '11px' }}>{label.toUpperCase()}</Tag>;
    };

    const fetchMetrics = async (isInitial = false) => {
        if (isInitial) setLoading(true);
        setIsUpdating(true);
        try {
            const response = await dashboardService.getMetrics();
            if (response.status === 'success') {
                setMetrics(response.data);
            }
        } catch (error) {
            console.error("Error fetching dashboard metrics", error);
            if (isInitial) message.error("No se pudieron cargar las métricas del dashboard");
        } finally {
            if (isInitial) setLoading(false);
            setLastUpdate(new Date());
            setTimeout(() => setIsUpdating(false), 1000);
        }
    };

    useEffect(() => {
        fetchMetrics(true);
        const interval = setInterval(() => fetchMetrics(), 2000);
        return () => clearInterval(interval);
    }, []);

    const campaignColumns = [
        {
            title: 'Campaña',
            dataIndex: 'name',
            key: 'name',
            render: (text) => <Text strong>{text}</Text>
        },
        {
            title: 'Tipo',
            dataIndex: 'type',
            key: 'type',
            render: (type) => {
                const label = type === 'inbound' ? 'ENTRANTE' : (type === 'outbound' ? 'SALIENTE' : type.toUpperCase());
                return (
                    <Tag color={type === 'inbound' ? 'cyan' : 'geekblue'}>
                        {label}
                    </Tag>
                );
            }
        },
        {
            title: 'Llamadas Hoy',
            dataIndex: 'calls_today',
            key: 'calls_today',
            render: (count) => <Text strong style={{ fontSize: '16px' }}>{count}</Text>
        },
        {
            title: 'Resultado',
            key: 'results',
            render: (_, record) => (
                <Space split={<Text type="secondary">|</Text>}>
                    <Space size={4}>
                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                        <Text title="Contestadas">{record.answered_today}</Text>
                    </Space>
                    <Space size={4}>
                        <ClockCircleOutlined style={{ color: '#faad14' }} />
                        <Text title="No contestadas / Fallidas">{record.failed_today}</Text>
                    </Space>
                </Space>
            )
        }
    ];

    const activeCallsColumns = [
        {
            title: 'Inicio',
            dataIndex: 'call_start',
            key: 'call_start',
            render: (text) => <Text style={{ fontSize: '12px' }}>{new Date(text).toLocaleTimeString()}</Text>
        },
        {
            title: 'Campaña',
            dataIndex: 'campaign_name',
            key: 'campaign_name',
            render: (text) => <Tag color="blue">{text || 'N/A'}</Tag>
        },
        {
            title: 'Origen',
            dataIndex: 'source',
            key: 'source',
            render: (text) => <Text strong>{text}</Text>
        },
        {
            title: 'Destino',
            dataIndex: 'destination',
            key: 'destination',
            render: (text) => <Text strong>{text}</Text>
        },
        {
            title: 'Estado',
            dataIndex: 'status',
            key: 'status',
            render: (status) => getStatusTag(status)
        },
        {
            title: 'DID',
            dataIndex: 'did',
            key: 'did',
        },
        {
            title: 'Espera / Hablado',
            key: 'timers',
            render: (_, record) => (
                <RealTimeTimer 
                    start={record.call_start} 
                    answer={record.call_answer} 
                    waitSeconds={record.wait_seconds}
                    formatTime={formatTime}
                />
            )
        },
        {
            title: 'Acciones',
            key: 'actions',
            align: 'right',
            render: (_, record) => {
                let extension = null;
                try {
                    if (record.sip_extension) {
                        const sipData = JSON.parse(record.sip_extension);
                        extension = sipData.extension;
                    }
                } catch (e) {
                    extension = record.sip_extension;
                }

                const canMonitor = canGlobalCall && extension;

                return (
                    <Space>
                        <Tooltip title={!canMonitor ? "Solo disponible en llamada" : "Escuchar"}>
                            <Button 
                                size="small" 
                                shape="circle"
                                icon={<CustomerServiceOutlined />} 
                                disabled={!canMonitor}
                                style={{ color: canMonitor ? '#1890ff' : undefined }}
                                onClick={() => {
                                    message.info(`Escuchando a ${record.agent_name}...`);
                                    makeCall(`*98${extension}`, `Escucha a ${record.agent_name}`);
                                }}
                            />
                        </Tooltip>
                        <Tooltip title={!canMonitor ? "Solo disponible en llamada" : "Susurrar"}>
                            <Button 
                                size="small" 
                                shape="circle"
                                icon={<MessageOutlined />} 
                                disabled={!canMonitor}
                                style={{ color: canMonitor ? '#52c41a' : undefined }}
                                onClick={() => {
                                    message.info(`Susurrando a ${record.agent_name}...`);
                                    makeCall(`*99${extension}`, `Susurro a ${record.agent_name}`);
                                }}
                            />
                        </Tooltip>
                    </Space>
                );
            }
        }
    ];

    const lastCallsColumns = [
        {
            title: 'Hora',
            dataIndex: 'call_start',
            key: 'call_start',
            render: (text) => <Text style={{ fontSize: '12px' }}>{new Date(text).toLocaleTimeString()}</Text>
        },
        {
            title: 'Campaña',
            dataIndex: 'campaign_name',
            key: 'campaign_name',
            render: (text) => <Tag color="default" style={{ fontSize: '11px' }}>{text || 'N/A'}</Tag>
        },
        {
            title: 'Origen',
            dataIndex: 'source',
            key: 'source',
            render: (text) => <Text strong>{text}</Text>
        },
        {
            title: 'Destino',
            dataIndex: 'destination',
            key: 'destination',
        },
        {
            title: 'Estado',
            dataIndex: 'status',
            key: 'status',
            render: (status) => getStatusTag(status)
        },
        {
            title: 'T. Espera',
            dataIndex: 'wait_seconds',
            key: 'wait_seconds',
            render: (sec) => <Text style={{ fontSize: '11px' }}>{formatTime(sec)}</Text>
        },
        {
            title: 'T. Hablado',
            dataIndex: 'talk_seconds',
            key: 'talk_seconds',
            render: (sec) => <Text strong>{formatTime(sec)}</Text>
        }
    ];

    if (!metrics && loading) {
        return (
            <div style={{ textAlign: 'center', padding: '100px' }}>
                <SyncOutlined spin style={{ fontSize: '48px', color: '#E60000' }} />
                <div style={{ marginTop: '16px' }}>Cargando métricas...</div>
            </div>
        );
    }

    return (
        <div className="fade-in">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <div>
                    <Title level={2} style={{ margin: 0 }}>Dashboard Operativo</Title>
                    <Text type="secondary">Resumen ejecutivo del estado del Call Center en tiempo real.</Text>
                </div>
                <Tag 
                    icon={<SyncOutlined spin={loading || isUpdating} />} 
                    color="blue"
                    className={isUpdating ? 'update-blink' : ''}
                    style={{ transition: 'all 0.3s' }}
                >
                    Actualizado: {lastUpdate.toLocaleTimeString()}
                </Tag>
            </div>


            <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} lg={6}>
                    <Card bordered={false} className="dashboard-card">
                        <Statistic
                            title="Total Agentes"
                            value={metrics?.agent_status.total || 0}
                            prefix={<TeamOutlined style={{ color: '#1890ff' }} />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card bordered={false} className="dashboard-card" style={{ borderLeft: '4px solid #52c41a' }}>
                        <Statistic
                            title="Disponibles"
                            value={metrics?.agent_status.available || 0}
                            valueStyle={{ color: '#3f8600' }}
                            prefix={<CheckCircleOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card bordered={false} className="dashboard-card" style={{ borderLeft: '4px solid #f5222d' }}>
                        <Statistic
                            title="Ocupados"
                            value={metrics?.agent_status.occupied || 0}
                            valueStyle={{ color: '#cf1322' }}
                            prefix={<PhoneOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card bordered={false} className="dashboard-card" style={{ borderLeft: '4px solid #faad14' }}>
                        <Statistic
                            title="En Pausa"
                            value={metrics?.agent_status.on_break || 0}
                            valueStyle={{ color: '#d48806' }}
                            prefix={<ClockCircleOutlined />}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                <Col span={24}>
                    <Card
                        title={<Space><PhoneOutlined /> Llamadas Activas</Space>}
                        bordered={false}
                        style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
                        extra={<Badge status="processing" text="En tiempo real" />}
                    >
                        <Table
                            columns={activeCallsColumns}
                            dataSource={metrics?.active_calls || []}
                            rowKey="id"
                            pagination={false}
                            loading={loading}
                            size="small"
                            locale={{ emptyText: 'No hay llamadas activas en este momento' }}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                <Col span={24}>
                    <Card
                        title={<Space><DashboardOutlined /> Rendimiento de Campañas (Hoy)</Space>}
                        bordered={false}
                        style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
                    >
                        <Table
                            columns={campaignColumns}
                            dataSource={metrics?.campaign_performance || []}
                            rowKey="id"
                            pagination={false}
                            loading={loading}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                <Col span={24}>
                    <Card
                        title={<Space><HistoryOutlined /> Últimas Llamadas (Hoy)</Space>}
                        bordered={false}
                        style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
                    >
                        <Table
                            columns={lastCallsColumns}
                            dataSource={metrics?.last_calls || []}
                            rowKey="id"
                            pagination={false}
                            loading={loading}
                            size="small"
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[24, 24]}>
                <Col span={24}>
                    <Card bordered={false} style={{ borderRadius: '12px', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
                        <Row align="middle" gutter={48}>
                            <Col xs={24} md={10}>
                                <Space size="large">
                                    <div style={{ background: '#E60000', padding: '12px', borderRadius: '12px' }}>
                                        <SafetyCertificateOutlined style={{ fontSize: '32px', color: '#fff' }} />
                                    </div>
                                    <div>
                                        <Text type="secondary" style={{ fontSize: '14px', display: 'block', fontWeight: 'bold' }}>LICENCIAS FACTURABLES</Text>
                                        <Title level={2} style={{ margin: 0, color: '#E60000' }}>
                                            {metrics?.licenses?.active_licenses?.total || 0}
                                        </Title>
                                        <Text size="small" type="secondary">
                                            {metrics?.licenses?.active_licenses?.agent || 0} Agentes | {metrics?.licenses?.active_licenses?.admin || 0} Admins
                                        </Text>
                                    </div>
                                </Space>
                            </Col>
                            <Col xs={24} md={14}>
                                <Row gutter={24}>
                                    <Col span={12}>
                                        <Statistic
                                            title="Usuarios Activos (Total)"
                                            value={metrics?.licenses?.active_users?.total || 0}
                                            valueStyle={{ color: '#1890ff' }}
                                            prefix={<TeamOutlined />}
                                            suffix={<Text type="secondary" style={{ fontSize: '14px' }}>Cuentas</Text>}
                                        />
                                    </Col>
                                    <Col span={12}>
                                        <Statistic
                                            title="Usuarios no facturables"
                                            value={(metrics?.licenses?.active_users?.total || 0) - (metrics?.licenses?.active_licenses?.total || 0)}
                                            valueStyle={{ color: '#faad14' }}
                                            suffix={<Text type="secondary" style={{ fontSize: '14px' }}>Exentos</Text>}
                                        />
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default DashboardPage;
